/**
 * Cena 3D do CCO › Mapa de Comando: maquete viva da rede de saneamento — Nexora Grid (US-4402).
 *
 * Criado em: 01/11/2026 · Última modificação: 02/11/2026 · Versão: 1.1.0
 *
 * three.js imperativo (sem o `Html` do drei, D-018), montado e desmontado pela tela `MapaComando.tsx`:
 * - CCO no centro: o gestor (IA) é um holograma sobre a mesa; os agentes, hologramas em volta; as pessoas reais
 *   (operador, supervisor, gerente), sólidas e uniformizadas. Holograma = IA, sólido = pessoa;
 * - setores em plataformas no anel, com os equipamentos reais (biblioteca 3D da US-4401), anel de estado no chão,
 *   feixe e luz no alarme, adutora transparente com água correndo e arco de dados até o CCO;
 * - telemetria: placa por equipamento (medida principal; o resto de perto), régua de nível nos reservatórios e a
 *   pressão da rede sobre a adutora (cor do tubo pela NBR 12218);
 * - eventos viram pulsos de luz pela rota (pessoa em campo → gestor → equipamento) e balões com a intenção
 *   (nunca o texto da conversa).
 * Só observação: nada aqui aciona equipamento.
 */
import * as THREE from "three";
import { MODELOS_SANEAMENTO } from "../modelosSaneamento";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { classeNivel, disporSetores, classePressao, larguraSetor, posicoesNoSetor, type EstadoMapa, type ModeloMapa,
  type ModeloPessoa } from "./regrasMapa";

export type EquipCena = { id: string; nome: string; modelo: ModeloMapa; estado: EstadoMapa };
export type SetorCena = { id: string; titulo: string; estado: EstadoMapa; equipamentos: EquipCena[] };
export type AtorCena = { id: string; nome: string; modelo: ModeloPessoa; estado: "ocioso" | "ativo" | "aprov" };
export type PlacaCena = { principal: string; resto: string[]; nivelPct: number | null };
export type AlvoCena = { tipo: "equipamento" | "setor" | "agente" | "pessoa"; id: string };
type Rotulo = { el: HTMLDivElement; obter: () => THREE.Vector3 | null; tipo: string; id?: string };

const ARQUIVO: Record<ModeloMapa | ModeloPessoa, string> = MODELOS_SANEAMENTO;
const ESCALA: Partial<Record<ModeloMapa, number>> = { reservatorio: 0.72, elevado: 0.5, poco: 1.7, pressao: 1.7, macro: 1.6, vrp: 1.6, motobomba: 1.7 };
const ESCALA_PESSOA = 1.35;
const COR_ESTADO: Record<EstadoMapa, number> = { ok: 0x56e0d0, atencao: 0xf0b13a, alarme: 0xff5348, offline: 0x6b7080 };
const COR_NIVEL = { ok: 0x2f8fd8, baixo: 0xf0b13a, critico: 0xff5348, sd: 0x5c6270 };
const COR_TUBO = { ok: 0x2a6f9e, alta: 0x9a7420, baixa: 0x9e2f2a, sd: 0x3a3f47 };
const reduzirMovimento = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Material holográfico (fresnel + linhas de varredura): a assinatura visual da IA. */
function holograma(cor: string): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { t: { value: 0 }, cor: { value: new THREE.Color(cor) } },
    vertexShader: "varying vec3 vN;varying vec3 vW;void main(){vN=normalize(normalMatrix*normal);vec4 w=modelMatrix*vec4(position,1.);vW=w.xyz;gl_Position=projectionMatrix*viewMatrix*w;}",
    fragmentShader: "uniform float t;uniform vec3 cor;varying vec3 vN;varying vec3 vW;void main(){float f=pow(1.-abs(vN.z),2.2);float s=.6+.4*sin(vW.y*38.-t*5.);gl_FragColor=vec4(cor*(.55+f*1.2),(.10+f*.85)*s);}",
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
  });
}

function texturaBrilho(): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const x = c.getContext("2d")!;
  const g = x.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, "rgba(255,255,255,1)"); g.addColorStop(0.3, "rgba(255,255,255,.55)"); g.addColorStop(1, "rgba(255,255,255,0)");
  x.fillStyle = g; x.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
}

export class CenaMapa {
  private renderer: THREE.WebGLRenderer;
  private cena = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(42, 1, 0.1, 500);
  private controles: OrbitControls;
  private modelos = new Map<string, THREE.Object3D>();
  private camadaRotulos: HTMLDivElement;
  private rotulos: Rotulo[] = [];
  private pick: THREE.Object3D[] = [];
  private ancoras = new Map<string, () => THREE.Vector3>();
  private grupoRede = new THREE.Group();
  private grupoAtores = new THREE.Group();
  private equip = new Map<string, { obj: THREE.Object3D; grp: THREE.Group; setor: string; x: number; z: number; altura: number; estado: EstadoMapa;
    anel: THREE.Mesh; rot: Rotulo; regua?: { agua: THREE.Mesh; alvo: number } }>();
  private setores = new Map<string, { grp: THREE.Group; tubo: THREE.MeshStandardMaterial; rotDuto: Rotulo }>();
  private agentes = new Map<string, { obj: THREE.Object3D; disco: THREE.Mesh; estado: AtorCena["estado"]; rot: Rotulo }>();
  private pessoas = new Map<string, THREE.Object3D>();
  private emCampo: { chave: string; obj: THREE.Object3D; rot: Rotulo; ate: number; pos: THREE.Vector3 }[] = [];
  private bolhas: { el: HTMLDivElement; ancora: string; ate: number }[] = [];
  private holos: THREE.ShaderMaterial[] = [];
  private holoGestor = holograma("#56e0d0");
  private holoAgente = holograma("#6fb6d8");
  private holoAprov = holograma("#f0b13a");
  private gestor?: THREE.Object3D;
  private animados: { tipo: "viga" | "luz" | "giro"; obj: THREE.Object3D }[] = [];
  private dutos: { curva: THREE.CatmullRomCurve3; gotas: THREE.InstancedMesh; qtd: number; vel: number }[] = [];
  private arcos: { curva: THREE.QuadraticBezierCurve3; offline: boolean; mqtt: boolean }[] = [];
  private pulsos: { curvas: THREE.Curve<THREE.Vector3>[]; t: number; cor: THREE.Color; rastro: number; vel: number }[] = [];
  private pPos: Float32Array; private pCor: Float32Array; private pGeo = new THREE.BufferGeometry();
  private readonly MAXP = 420;
  private quadro = 0; private ultimoPacote = 0; private inicio = performance.now();
  private voo: { pos: THREE.Vector3; alvo: THREE.Vector3 } | null = null;
  private vista = { pos: new THREE.Vector3(0, 30, 41), alvo: new THREE.Vector3(0, 0, 1) };
  private larguraMax = 11.5;
  private raioExterno = 21.5;
  private centrosSetores: THREE.Vector3[] = [];
  private selecionado: string | null = null;
  private setorSelecionado: string | null = null;
  private filtro: Set<string> | null = null;
  private obs: ResizeObserver;
  private baixo: [number, number] | null = null;
  private tmp = new THREE.Vector3(); private proj = new THREE.Vector3(); private mat4 = new THREE.Matrix4();
  aoSelecionar: (alvo: AlvoCena | null) => void = () => undefined;
  aoPassar: (alvo: AlvoCena | null, x: number, y: number) => void = () => undefined;

  constructor(private palco: HTMLDivElement) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.domElement.className = "mc-canvas";
    palco.prepend(this.renderer.domElement);
    this.camadaRotulos = document.createElement("div");
    this.camadaRotulos.className = "mc-rotulos";
    palco.appendChild(this.camadaRotulos);
    this.holos.push(this.holoGestor, this.holoAgente, this.holoAprov);

    this.cena.fog = new THREE.Fog(0x0a0f13, 60, 140);
    this.camera.position.copy(this.vista.pos);
    this.controles = new OrbitControls(this.camera, this.renderer.domElement);
    Object.assign(this.controles, { enableDamping: true, dampingFactor: 0.08, autoRotateSpeed: 0.25, autoRotate: !reduzirMovimento(),
      maxPolarAngle: Math.PI * 0.47, minDistance: 6, maxDistance: 140 });
    this.controles.target.copy(this.vista.alvo);

    this.cena.add(new THREE.HemisphereLight(0xc6d8e6, 0x1a2328, 1.2));
    const sol = new THREE.DirectionalLight(0xfff4e6, 2.4);
    sol.position.set(18, 32, 14);
    sol.castShadow = true;
    sol.shadow.mapSize.set(2048, 2048);
    Object.assign(sol.shadow.camera, { left: -45, right: 45, top: 45, bottom: -45, near: 1, far: 110 });
    sol.shadow.bias = -0.0005;
    this.cena.add(sol);
    const chao = new THREE.Mesh(new THREE.CircleGeometry(120, 64), new THREE.MeshStandardMaterial({ color: 0x0e151a, roughness: 1 }));
    chao.rotation.x = -Math.PI / 2;
    chao.receiveShadow = true;
    this.cena.add(chao, new THREE.PolarGridHelper(70, 24, 12, 64, 0x16232b, 0x121c22));
    this.cena.add(this.grupoRede, this.grupoAtores);

    this.pPos = new Float32Array(this.MAXP * 3);
    this.pCor = new Float32Array(this.MAXP * 3);
    this.pGeo.setAttribute("position", new THREE.BufferAttribute(this.pPos, 3));
    this.pGeo.setAttribute("color", new THREE.BufferAttribute(this.pCor, 3));
    this.cena.add(new THREE.Points(this.pGeo, new THREE.PointsMaterial({ size: 0.9, map: texturaBrilho(), vertexColors: true, transparent: true,
      depthWrite: false, blending: THREE.AdditiveBlending })));

    this.montarCco();
    this.obs = new ResizeObserver(() => requestAnimationFrame(() => this.ajustar()));
    this.obs.observe(palco);
    this.ajustar();
    const el = this.renderer.domElement;
    el.addEventListener("pointermove", this.mover);
    el.addEventListener("pointerleave", this.sair);
    el.addEventListener("pointerdown", this.descer);
    el.addEventListener("pointerup", this.subir);
    this.renderer.setAnimationLoop(this.laco);
  }

  /** Baixa os modelos da biblioteca 3D (US-4401). */
  async carregar(base = "/modelos/saneamento/"): Promise<void> {
    const loader = new GLTFLoader();
    await Promise.all(Object.entries(ARQUIVO).map(([k, arq]) => loader.loadAsync(`${base}${arq}.glb`).then((g) => { this.modelos.set(k, g.scene); })));
    this.montarAtoresFixos();
  }

  private instancia(k: ModeloMapa | ModeloPessoa, material?: THREE.Material): THREE.Object3D {
    const base = this.modelos.get(k);
    const o = base ? base.clone(true) : new THREE.Mesh(new THREE.BoxGeometry(0.8, 1.2, 0.8), new THREE.MeshStandardMaterial({ color: 0x7d9ca7 }));
    o.traverse((m) => {
      if ((m as THREE.Mesh).isMesh) {
        const malha = m as THREE.Mesh;
        malha.castShadow = !material; malha.receiveShadow = !material;
        malha.material = material ?? (malha.material as THREE.Material).clone();
      }
    });
    if (k === "tecnico" || k === "gestor" || k === "operador" || k === "gerente") o.scale.setScalar(ESCALA_PESSOA);
    return o;
  }

  private rotulo(tipo: string, html: string, obter: () => THREE.Vector3 | null, id?: string): Rotulo {
    const el = document.createElement("div");
    el.className = `mc-rot ${tipo}`;
    el.innerHTML = html;
    this.camadaRotulos.appendChild(el);
    const r = { el, obter, tipo, id };
    this.rotulos.push(r);
    return r;
  }

  private tirarRotulo(r: Rotulo) {
    r.el.remove();
    this.rotulos = this.rotulos.filter((x) => x !== r);
  }

  private montarCco() {
    const cco = new THREE.Group();
    this.cena.add(cco);
    const base = new THREE.Mesh(new THREE.CylinderGeometry(6.4, 6.8, 0.35, 72), new THREE.MeshStandardMaterial({ color: 0x18242c, roughness: 0.7, metalness: 0.3 }));
    base.position.y = 0.17; base.receiveShadow = true; cco.add(base);
    const borda = new THREE.Mesh(new THREE.TorusGeometry(6.45, 0.04, 8, 96), new THREE.MeshBasicMaterial({ color: 0x56e0d0 }));
    borda.rotation.x = Math.PI / 2; borda.position.y = 0.36; cco.add(borda);
    const mesa = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.7, 0.85, 48), new THREE.MeshStandardMaterial({ color: 0x1d2b34, roughness: 0.5, metalness: 0.5 }));
    mesa.position.y = 0.78; mesa.castShadow = true; cco.add(mesa);
    const tampo = new THREE.Mesh(new THREE.CircleGeometry(1.42, 48), new THREE.MeshBasicMaterial({ color: 0x56e0d0, transparent: true, opacity: 0.18 }));
    tampo.rotation.x = -Math.PI / 2; tampo.position.y = 1.215; cco.add(tampo);
    const anel = new THREE.Mesh(new THREE.TorusGeometry(1.2, 0.025, 6, 64), new THREE.MeshBasicMaterial({ color: 0x56e0d0, transparent: true, opacity: 0.8 }));
    anel.rotation.x = Math.PI / 2; anel.position.y = 1.24; cco.add(anel);
    this.animados.push({ tipo: "giro", obj: anel });
    const cone = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 1.35, 2.6, 40, 1, true), new THREE.MeshBasicMaterial({ color: 0x56e0d0, transparent: true,
      opacity: 0.05, side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending }));
    cone.position.y = 2.55; cco.add(cone);
    this.ancoras.set("g", () => new THREE.Vector3(0, 3.2, 0));
    this.rotulo("gestor", "Gestor", () => this.tmp.set(0, 4.35, 0));
  }

  private montarAtoresFixos() {
    const g = this.instancia("gestor", this.holoGestor);
    g.scale.setScalar(1.6); g.position.set(0, 1.25, 0);
    g.userData.alvo = { tipo: "agente", id: "gestor" } satisfies AlvoCena;
    this.cena.add(g); this.pick.push(g); this.gestor = g;
  }

  /** Agentes (hologramas em volta da mesa) e pessoas do CCO (sólidas, na borda). Remonta só se a lista mudar. */
  definirAtores(agentes: AtorCena[], pessoas: AtorCena[]) {
    if (!this.modelos.size) return;
    const assinatura = agentes.map((a) => a.id).join(",") + "|" + pessoas.map((p) => p.id + p.modelo).join(",");
    if (this.grupoAtores.userData.assinatura !== assinatura) {
      this.limparGrupo(this.grupoAtores);
      this.agentes.forEach((a) => this.tirarRotulo(a.rot));
      this.agentes.clear(); this.pessoas.clear();
      agentes.forEach((a, i) => {
        const b = (i / Math.max(agentes.length, 1)) * Math.PI * 2 + Math.PI / Math.max(agentes.length, 1);
        const f = this.instancia("operador", this.holoAgente);
        f.position.set(Math.cos(b) * 3.6, 0.35, Math.sin(b) * 3.6); f.lookAt(0, 0.35, 0);
        f.userData.alvo = { tipo: "agente", id: a.id } satisfies AlvoCena;
        const disco = new THREE.Mesh(new THREE.RingGeometry(0.38, 0.46, 40), new THREE.MeshBasicMaterial({ color: 0x6fb6d8, transparent: true, opacity: 0.4, side: THREE.DoubleSide }));
        disco.rotation.x = -Math.PI / 2; disco.position.set(f.position.x, 0.37, f.position.z);
        this.grupoAtores.add(f, disco); this.pick.push(f);
        const p = f.position.clone();
        this.ancoras.set(`a:${a.id}`, () => p.clone().setY(1.8));
        const rot = this.rotulo("agente", a.nome, () => this.tmp.copy(p).setY(2.75), a.id);
        this.agentes.set(a.id, { obj: f, disco, estado: a.estado, rot });
      });
      // como no protótipo: as pessoas ficam na frente da mesa (lado da câmera); o operador num console com dois monitores
      const lista = pessoas.slice(0, 8);
      lista.forEach((pe, i) => {
        const b = Math.PI * 0.8 - (lista.length > 1 ? (i / (lista.length - 1)) * Math.PI * 0.55 : 0);
        const o = this.instancia(pe.modelo);
        o.position.set(Math.cos(b) * 5.6, 0.35, Math.sin(b) * 5.6); o.lookAt(0, 0.35, 0);
        if (pe.modelo === "operador") {
          const console = new THREE.Group();
          console.position.copy(o.position); console.lookAt(0, 0.35, 0);
          const mesa = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.75, 0.7), new THREE.MeshStandardMaterial({ color: 0x26343d, roughness: 0.6 }));
          mesa.position.set(0, 0.37, 0.65); mesa.castShadow = true; console.add(mesa);
          [-0.42, 0.42].forEach((x) => {
            const tela = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.42, 0.04), new THREE.MeshStandardMaterial({ color: 0x111111, emissive: 0x2a8f86, emissiveIntensity: 0.9 }));
            tela.position.set(x, 1.05, 0.92); tela.rotation.y = Math.PI; console.add(tela);
          });
          this.grupoAtores.add(console);
        }
        o.userData.alvo = { tipo: "pessoa", id: pe.id } satisfies AlvoCena;
        this.grupoAtores.add(o); this.pick.push(o); this.pessoas.set(pe.id, o);
        const p = o.position.clone();
        this.ancoras.set(`p:${pe.id}`, () => p.clone().setY(2.1));
        this.rotulo("pessoa", pe.nome, () => this.tmp.copy(p).setY(2.85), pe.id).el.dataset.grupo = "atores";
      });
      this.grupoAtores.userData.assinatura = assinatura;
    }
    agentes.forEach((a) => { const x = this.agentes.get(a.id); if (x) x.estado = a.estado; });
  }

  /** Setores e equipamentos. Remonta só se a estrutura mudar (estados e telemetria vêm em `atualizar`). */
  montarRede(setores: SetorCena[]) {
    if (!this.modelos.size) return;
    const assinatura = setores.map((s) => s.id + ":" + s.equipamentos.map((e) => e.id + e.modelo + e.estado).join(",")).join("|");
    if (this.grupoRede.userData.assinatura === assinatura) return;
    this.limparGrupo(this.grupoRede);
    this.equip.forEach((e) => this.tirarRotulo(e.rot));
    this.setores.forEach((s) => this.tirarRotulo(s.rotDuto));
    this.rotulos.filter((r) => r.tipo === "setor").forEach((r) => this.tirarRotulo(r));
    this.equip.clear(); this.setores.clear(); this.dutos = []; this.arcos = [];
    this.animados = this.animados.filter((a) => a.tipo === "giro");
    const layout = disporSetores(setores.map((s) => larguraSetor(s.equipamentos.length)));
    this.raioExterno = layout.raio;
    this.centrosSetores = layout.posicoes.map((p) => new THREE.Vector3(p.x, 0, p.z));
    setores.forEach((s, i) => {
      const { x, z, giro } = layout.posicoes[i];
      const a = Math.atan2(z, x);
      const grp = new THREE.Group();
      grp.position.set(x, 0, z);
      grp.rotation.y = giro;
      this.grupoRede.add(grp);
      grp.updateMatrixWorld(true);
      const largura = larguraSetor(s.equipamentos.length);
      const piso = new THREE.Mesh(new THREE.BoxGeometry(largura, 0.3, 7.2), new THREE.MeshStandardMaterial({ color: 0x1a262e, roughness: 0.9 }));
      piso.position.y = 0.15; piso.receiveShadow = true;
      piso.userData.alvo = { tipo: "setor", id: s.id } satisfies AlvoCena;
      grp.add(piso); this.pick.push(piso);
      const contorno = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(largura, 0.3, 7.2)),
        new THREE.LineBasicMaterial({ color: s.estado === "ok" ? 0x3c5866 : COR_ESTADO[s.estado] }));
      contorno.position.y = 0.15; grp.add(contorno);
      const pos = posicoesNoSetor(s.equipamentos.length);
      const pts: THREE.Vector3[] = [];
      s.equipamentos.forEach((e, j) => {
        const { x, z } = pos[j];
        const o = this.instancia(e.modelo);
        o.scale.multiplyScalar(ESCALA[e.modelo] ?? 1);
        o.position.set(x, 0.3, z);
        grp.add(o);
        if (e.estado === "offline") o.traverse((m) => {
          const mat = (m as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
          if ((m as THREE.Mesh).isMesh && mat) { mat.color.lerp(new THREE.Color(0x777b84), 0.75); mat.transparent = true; mat.opacity = 0.55; if (mat.emissive) mat.emissiveIntensity = 0; }
        });
        o.userData.alvo = { tipo: "equipamento", id: e.id } satisfies AlvoCena;
        this.pick.push(o);
        const caixa = new THREE.Box3().setFromObject(o);
        const altura = caixa.max.y - caixa.min.y;
        pts.push(new THREE.Vector3(x, 0.32, z + (z > 0 ? -0.54 : 0.54)));
        const raioAnel = Math.min(Math.max(caixa.max.x - caixa.min.x, caixa.max.z - caixa.min.z) / 2 + 0.2, 2.1);
        const anel = new THREE.Mesh(new THREE.RingGeometry(raioAnel, raioAnel + 0.09, 48), new THREE.MeshBasicMaterial({ color: COR_ESTADO[e.estado],
          transparent: true, opacity: e.estado === "ok" ? 0.35 : 0.9, side: THREE.DoubleSide }));
        anel.rotation.x = -Math.PI / 2; anel.position.set(x, 0.32, z); grp.add(anel);
        if (e.estado === "alarme") {
          const viga = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.6, 9, 24, 1, true), new THREE.MeshBasicMaterial({ color: 0xff5348, transparent: true,
            opacity: 0.16, side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending }));
          viga.position.set(x, 4.8, z); grp.add(viga);
          const luz = new THREE.PointLight(0xff3b30, 8, 8, 1); luz.position.set(x, altura + 1.2, z); grp.add(luz);
          this.animados.push({ tipo: "viga", obj: viga }, { tipo: "luz", obj: luz });
        }
        this.ancoras.set(`d:${e.id}`, () => grp.localToWorld(new THREE.Vector3(x, altura + 0.6, z)));
        const rot = this.rotulo(`disp ${e.estado}`, `<span class="cab"><span class="pt"></span>${escapar(e.nome)}</span><span class="tel"><span class="prin">—</span><span class="resto"></span></span>`,
          () => grp.localToWorld(this.tmp.set(x, altura + 0.95, z)), e.id);
        let regua: { agua: THREE.Mesh; alvo: number } | undefined;
        if (e.modelo === "reservatorio" || e.modelo === "elevado") {
          const rx = Math.max(caixa.max.x - caixa.min.x, caixa.max.z - caixa.min.z) / 2;
          const h = e.modelo === "elevado" ? 2.6 : Math.min(altura * 0.85, 3.0);
          const r = new THREE.Group(); r.position.set(x + Math.min(rx, 1.7) + 0.45, 0.32, z); grp.add(r);
          const vidro = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.24, h, 20, 1, true), new THREE.MeshStandardMaterial({ color: 0xcfe3ee, transparent: true,
            opacity: 0.16, roughness: 0.1, depthWrite: false, side: THREE.DoubleSide }));
          vidro.position.y = h / 2; r.add(vidro);
          [0, 0.25, 0.5, 0.75, 1].forEach((f) => { const m = new THREE.Mesh(new THREE.TorusGeometry(0.25, 0.014, 6, 24), new THREE.MeshBasicMaterial({ color: 0x8fa6b2 })); m.rotation.x = Math.PI / 2; m.position.y = f * h; r.add(m); });
          const g = new THREE.CylinderGeometry(0.2, 0.2, 1, 20); g.translate(0, 0.5, 0);
          const agua = new THREE.Mesh(g, new THREE.MeshStandardMaterial({ color: COR_NIVEL.ok, emissive: 0x1a5a99, emissiveIntensity: 0.9, roughness: 0.3 }));
          agua.scale.y = 0.001; agua.userData.h = h; r.add(agua);
          regua = { agua, alvo: 0 };
        }
        this.equip.set(e.id, { obj: o, grp, setor: s.id, x, z, altura, estado: e.estado, anel, rot, regua });
      });
      const meia = largura / 2 - 0.2;
      const curva = new THREE.CatmullRomCurve3([new THREE.Vector3(-meia, 0.32, 0), ...pts, new THREE.Vector3(meia, 0.32, 0)]);
      const tuboMat = new THREE.MeshStandardMaterial({ color: COR_TUBO.ok, transparent: true, opacity: 0.38, roughness: 0.2, metalness: 0.1, depthWrite: false });
      grp.add(new THREE.Mesh(new THREE.TubeGeometry(curva, 80, 0.13, 10, false), tuboMat));
      const qtd = s.estado === "offline" ? 0 : 16;
      const gotas = new THREE.InstancedMesh(new THREE.SphereGeometry(0.075, 8, 6), new THREE.MeshBasicMaterial({ color: 0x7fdcff }), Math.max(qtd, 1));
      gotas.count = qtd; grp.add(gotas);
      this.dutos.push({ curva, gotas, qtd, vel: 0.045 + Math.random() * 0.02 });
      this.ancoras.set(`s:${s.id}`, () => grp.localToWorld(new THREE.Vector3(0, 1.2, 0)));
      this.rotulo("setor", escapar(s.titulo), () => grp.localToWorld(this.tmp.set(0, 0.4, -4.1)), s.id);
      const rotDuto = this.rotulo("duto sd", "Rede sem dados", () => grp.localToWorld(this.tmp.set(-meia + 0.6, 0.95, 0)), s.id);
      this.setores.set(s.id, { grp, tubo: tuboMat, rotDuto });
      const ini = grp.localToWorld(new THREE.Vector3(0, 0.4, 3.6));
      const fim = new THREE.Vector3(Math.cos(a) * 6.6, 0.4, Math.sin(a) * 6.6);
      const meio = ini.clone().add(fim).multiplyScalar(0.5).setY(5.5);
      const arco = new THREE.QuadraticBezierCurve3(ini, meio, fim);
      const linha = new THREE.Line(new THREE.BufferGeometry().setFromPoints(arco.getPoints(48)),
        new THREE.LineDashedMaterial({ color: s.estado === "offline" ? 0x4a5059 : 0x2f6f78, dashSize: 0.6, gapSize: 0.35, transparent: true, opacity: 0.8 }));
      linha.computeLineDistances(); this.grupoRede.add(linha);
      this.arcos.push({ curva: arco, offline: s.estado === "offline", mqtt: false });
    });
    this.grupoRede.userData.assinatura = assinatura;
    this.larguraMax = Math.max(...setores.map((s) => larguraSetor(s.equipamentos.length)), 11.5);
    this.reenquadrar(false);
  }

  /** Telemetria nas placas, régua de nível e pressão da rede por setor. */
  atualizar(placas: Map<string, PlacaCena>, pressoes: Map<string, number | null>, mqtt: Set<string>) {
    placas.forEach((p, id) => {
      const e = this.equip.get(id);
      if (!e) return;
      (e.rot.el.querySelector(".prin") as HTMLElement).textContent = p.principal;
      e.rot.el.classList.toggle("semtel", p.principal === "Sem telemetria");
      (e.rot.el.querySelector(".resto") as HTMLElement).innerHTML = p.resto.map(escapar).join("<br>");
      if (e.regua) {
        e.regua.alvo = p.nivelPct === null ? 0 : Math.max(0, Math.min(1, p.nivelPct / 100));
        ((e.regua.agua.material) as THREE.MeshStandardMaterial).color.setHex(COR_NIVEL[classeNivel(p.nivelPct)]);
      }
    });
    this.setores.forEach((s, id) => {
      const v = pressoes.get(id) ?? null;
      const c = classePressao(v);
      s.rotDuto.el.className = `mc-rot duto ${c}`;
      s.rotDuto.el.textContent = `Rede ${v === null ? "sem dados" : v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + " mca"}`;
      s.tubo.color.setHex(COR_TUBO[c]);
    });
    this.arcos.forEach((a, i) => { a.mqtt = mqtt.has([...this.setores.keys()][i]); });
  }

  /** Pulso de luz pela rota de âncoras (pessoa em campo → gestor → equipamento). */
  pulsar(rota: string[], cor: string, bolha?: { texto: string; canal: string; classe?: string }) {
    const pts = rota.map((a) => this.resolver(a)).filter((v): v is THREE.Vector3 => !!v);
    if (pts.length > 1) {
      const curvas: THREE.Curve<THREE.Vector3>[] = [];
      for (let i = 0; i < pts.length - 1; i++) {
        const m = pts[i].clone().add(pts[i + 1]).multiplyScalar(0.5);
        m.y += Math.max(1.2, pts[i].distanceTo(pts[i + 1]) * 0.28);
        curvas.push(new THREE.QuadraticBezierCurve3(pts[i], m, pts[i + 1]));
      }
      this.pulsos.push({ curvas, t: 0, cor: new THREE.Color(cor), rastro: 7, vel: reduzirMovimento() ? 0.2 : 0.5 });
      if (this.pulsos.length > 50) this.pulsos.shift();
    }
    if (bolha && rota.length) {
      const el = document.createElement("div");
      el.className = `mc-bolha ${bolha.classe ?? ""}`;
      el.innerHTML = `<small>${escapar(bolha.canal)}</small>${escapar(bolha.texto)}`;
      this.camadaRotulos.appendChild(el);
      this.bolhas.push({ el, ancora: rota[0], ate: performance.now() + 6500 });
    }
  }

  private resolver(a: string): THREE.Vector3 | null {
    if (a.startsWith("f:")) {
      const [, ator, device] = a.split(":");
      return this.pessoaEmCampo(ator, device);
    }
    const f = this.ancoras.get(a) ?? (a.startsWith("a:") || a.startsWith("p:") ? this.ancoras.get("g") : undefined);
    return f ? f() : null;
  }

  private pessoaEmCampo(ator: string, device: string): THREE.Vector3 | null {
    const e = this.equip.get(device);
    if (!e) return null;
    const chave = `${ator}:${device}`;
    let p = this.emCampo.find((x) => x.chave === chave);
    if (!p) {
      const o = this.instancia("tecnico");
      const local = new THREE.Vector3(e.x + 0.6, 0.3, e.z + (e.z > 0 ? 1.9 : -1.9));
      o.position.copy(local); e.grp.add(o);
      o.lookAt(e.grp.localToWorld(new THREE.Vector3(e.x, 0.3, e.z)));
      o.userData.alvo = { tipo: "pessoa", id: ator } satisfies AlvoCena;
      this.pick.push(o);
      const rot = this.rotulo("pessoa", escapar(ator), () => e.grp.localToWorld(this.tmp.copy(local).setY(2.85)));
      p = { chave, obj: o, rot, ate: 0, pos: local };
      this.emCampo.push(p);
    }
    p.ate = performance.now() + 15000;
    return e.grp.localToWorld(p.pos.clone().setY(2.1));
  }

  /** Equipamento selecionado (anel pulsando) e setor selecionado (as placas do setor abrem completas, como no protótipo). */
  selecionar(id: string | null, setor: string | null = null) { this.selecionado = id; this.setorSelecionado = setor; }
  filtrar(ids: Set<string> | null) { this.filtro = ids; }
  girar(sim: boolean) { this.controles.autoRotate = sim; }
  visaoGeral() { this.voo = { pos: this.vista.pos.clone(), alvo: this.vista.alvo.clone() }; }

  /** Leva a câmera até o equipamento, setor ou agente. */
  voarPara(alvo: AlvoCena) {
    const chave = alvo.tipo === "equipamento" ? `d:${alvo.id}` : alvo.tipo === "setor" ? `s:${alvo.id}` : alvo.tipo === "agente" ? (alvo.id === "gestor" ? "g" : `a:${alvo.id}`) : `p:${alvo.id}`;
    const p = this.ancoras.get(chave)?.();
    if (!p) return;
    const dist = alvo.tipo === "setor" ? 16 : alvo.tipo === "equipamento" ? 11 : 9;
    const dir = new THREE.Vector3(p.x, 0, p.z);
    if (dir.lengthSq() < 1) dir.set(0, 0, 1);
    dir.normalize();
    this.voo = { alvo: p, pos: p.clone().add(dir.multiplyScalar(dist)).setY(p.y + dist * 0.6) };
  }

  private ajustar() {
    const w = this.palco.clientWidth, h = this.palco.clientHeight;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.reenquadrar(this.quadro === 0);
  }

  /** Vista geral: centrada nos setores (um setor só não fica colado na borda) e afastada conforme o anel e a largura da maior plataforma. */
  private reenquadrar(imediato: boolean) {
    const raio = this.raioExterno;
    const poucos = this.centrosSetores.length > 0 && this.centrosSetores.length <= 2;
    const centro = new THREE.Vector3();
    if (poucos) this.centrosSetores.forEach((c) => centro.add(c));
    if (poucos) centro.multiplyScalar(0.5 / this.centrosSetores.length);
    const k = Math.max(1, 1.62 / this.camera.aspect) * (raio / 21.5) * (raio > 22 ? 1.2 : 1) * Math.max(1, this.larguraMax / 17) * (poucos ? 0.72 : 1);
    const pos = new THREE.Vector3(0, 30, 41).multiplyScalar(k).add(centro);
    const alvo = new THREE.Vector3(0, 0, 1).add(centro);
    if (this.vista.pos.distanceTo(pos) < 0.1 && this.vista.alvo.distanceTo(alvo) < 0.1) return;
    this.vista.pos.copy(pos);
    this.vista.alvo.copy(alvo);
    // a névoa acompanha o tamanho da rede (com a câmera longe, névoa fixa apagaria tudo)
    const fog = this.cena.fog as THREE.Fog;
    fog.near = Math.max(60, pos.length() * 1.1);
    fog.far = Math.max(140, pos.length() * 3.4);
    if (imediato) { this.camera.position.copy(pos); this.controles.target.copy(alvo); }
    else this.voo = { pos: pos.clone(), alvo: alvo.clone() };
  }

  private alvoEm(e: PointerEvent): AlvoCena | null {
    const r = this.renderer.domElement.getBoundingClientRect();
    const ptr = new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ptr, this.camera);
    const h = ray.intersectObjects(this.pick, true)[0];
    let o: THREE.Object3D | null = h?.object ?? null;
    while (o && !o.userData.alvo) o = o.parent;
    return o ? (o.userData.alvo as AlvoCena) : null;
  }

  private mover = (e: PointerEvent) => {
    const a = this.alvoEm(e);
    this.renderer.domElement.style.cursor = a ? "pointer" : "";
    const r = this.palco.getBoundingClientRect();
    this.aoPassar(a, e.clientX - r.left, e.clientY - r.top);
  };
  private sair = () => this.aoPassar(null, 0, 0);
  private descer = (e: PointerEvent) => { this.baixo = [e.clientX, e.clientY]; };
  private subir = (e: PointerEvent) => {
    if (!this.baixo || Math.hypot(e.clientX - this.baixo[0], e.clientY - this.baixo[1]) > 5) return;
    this.aoSelecionar(this.alvoEm(e));
  };

  private projetar(v: THREE.Vector3, el: HTMLElement, W: number, H: number, sobe = 0): boolean {
    this.proj.copy(v).project(this.camera);
    if (this.proj.z > 1 || Math.abs(this.proj.x) > 1.2 || Math.abs(this.proj.y) > 1.2) { el.style.display = "none"; return false; }
    el.style.display = "";
    el.style.left = `${((this.proj.x + 1) / 2) * W}px`;
    el.style.top = `${((1 - this.proj.y) / 2) * H - sobe}px`;
    return true;
  }

  private laco = () => {
    const agora = performance.now(), t = (agora - this.inicio) / 1000;
    this.quadro++;
    this.holos.forEach((m) => { m.uniforms.t.value = t; });
    if (this.voo) {
      this.camera.position.lerp(this.voo.pos, 0.05);
      this.controles.target.lerp(this.voo.alvo, 0.05);
      if (this.camera.position.distanceTo(this.voo.pos) < 0.25) this.voo = null;
    }
    this.controles.update();
    if (this.gestor) this.gestor.rotation.y = t * 0.3;
    this.agentes.forEach((a) => {
      const mat = a.estado === "aprov" ? this.holoAprov : this.holoAgente;
      a.obj.traverse((m) => { if ((m as THREE.Mesh).isMesh && (m as THREE.Mesh).material !== mat) (m as THREE.Mesh).material = mat; });
      const d = a.disco.material as THREE.MeshBasicMaterial;
      d.opacity = a.estado === "ativo" ? 0.5 + 0.4 * Math.sin(t * 6) : 0.25;
      d.color.setHex(a.estado === "aprov" ? 0xf0b13a : 0x6fb6d8);
      a.rot.el.classList.toggle("aprov", a.estado === "aprov");
    });
    this.animados.forEach((a) => {
      if (a.tipo === "viga") ((a.obj as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = 0.1 + 0.1 * Math.sin(t * 4);
      else if (a.tipo === "luz") (a.obj as THREE.PointLight).intensity = 6 + 5 * Math.sin(t * 4);
      else a.obj.rotation.z = t * 0.6;
    });
    this.equip.forEach((e, id) => {
      const sel = this.selecionado === id, vis = !this.filtro || this.filtro.has(id);
      e.anel.scale.setScalar(sel ? 1.25 + 0.08 * Math.sin(t * 5) : 1);
      (e.anel.material as THREE.MeshBasicMaterial).opacity = !vis ? 0.08 : e.estado === "alarme" ? 0.55 + 0.4 * Math.sin(t * 5) : e.estado === "ok" ? 0.35 : 0.85;
      if (e.regua) e.regua.agua.scale.y += (Math.max(e.regua.alvo * (e.regua.agua.userData.h as number), 0.001) - e.regua.agua.scale.y) * 0.06;
    });
    this.dutos.forEach((d) => {
      for (let i = 0; i < d.qtd; i++) {
        d.curva.getPointAt(((t * d.vel) + i / d.qtd) % 1, this.proj);
        this.mat4.makeTranslation(this.proj.x, this.proj.y, this.proj.z);
        d.gotas.setMatrixAt(i, this.mat4);
      }
      if (d.qtd) d.gotas.instanceMatrix.needsUpdate = true;
    });
    if (agora - this.ultimoPacote > 900) {
      this.ultimoPacote = agora;
      this.arcos.forEach((a) => {
        if (a.offline || (!a.mqtt && Math.random() > 0.18)) return;
        this.pulsos.push({ curvas: [a.curva], t: 0, cor: new THREE.Color(a.mqtt ? "#56e0d0" : "#3d7f9a"), rastro: 3, vel: 0.6 });
      });
    }
    for (let i = this.emCampo.length - 1; i >= 0; i--) {
      const p = this.emCampo[i];
      if (agora > p.ate) { p.obj.parent?.remove(p.obj); this.tirarRotulo(p.rot); this.pick = this.pick.filter((x) => x !== p.obj); this.emCampo.splice(i, 1); }
    }
    let k = 0;
    this.pulsos = this.pulsos.filter((p) => p.t < p.curvas.length + 0.3);
    for (const p of this.pulsos) {
      p.t += p.vel * 0.016 * (1 + 0.4 / Math.max(1, p.curvas.length));
      for (let r = 0; r < p.rastro && k < this.MAXP; r++) {
        const tt = p.t - r * 0.035;
        if (tt < 0 || tt > p.curvas.length) continue;
        const ci = Math.min(Math.floor(tt), p.curvas.length - 1);
        p.curvas[ci].getPoint(Math.min(tt - ci, 1), this.proj);
        const f = 1 - r / p.rastro;
        this.pPos.set([this.proj.x, this.proj.y, this.proj.z], k * 3);
        this.pCor.set([p.cor.r * f, p.cor.g * f, p.cor.b * f], k * 3);
        k++;
      }
    }
    for (; k < this.MAXP; k++) this.pPos[k * 3 + 1] = -999;
    this.pGeo.attributes.position.needsUpdate = true;
    this.pGeo.attributes.color.needsUpdate = true;
    this.renderer.render(this.cena, this.camera);
    this.desenharRotulos(agora);
  };

  /** Rótulos e balões; placas sem sobreposição (alarme > atenção > sem dados > normal). */
  private desenharRotulos(agora: number) {
    const W = this.palco.clientWidth, H = this.palco.clientHeight;
    const pertoCco = this.controles.target.length() < 8 && this.camera.position.distanceTo(this.controles.target) < 26;
    this.rotulos.forEach((r) => {
      const v = r.obter();
      if (!v) { r.el.style.display = "none"; return; }
      this.projetar(v, r.el, W, H);
    });
    this.agentes.forEach((a, id) => { if (!(pertoCco || a.estado !== "ocioso" || this.selecionado === id)) a.rot.el.style.display = "none"; });
    const ocupados: { x: number; y: number; w: number; h: number }[] = [];
    const sobrepoe = (r: { x: number; y: number; w: number; h: number }) => ocupados.find((o) => r.x < o.x + o.w && r.x + r.w > o.x && r.y < o.y + o.h && r.y + r.h > o.y);
    const pri: Record<EstadoMapa, number> = { alarme: 0, atencao: 1, offline: 2, ok: 3 };
    [...this.equip.entries()].sort(([ia, a], [ib, b]) => (ia === this.selecionado ? -1 : pri[a.estado]) - (ib === this.selecionado ? -1 : pri[b.estado])).forEach(([id, e]) => {
      const el = e.rot.el;
      if (this.filtro && !this.filtro.has(id)) el.style.display = "none";
      const longe = this.camera.position.distanceTo(this.ancoras.get(`d:${id}`)!());
      // rede grande: na visão geral só os equipamentos que pedem atenção (ou o filtrado/selecionado) mostram rótulo; o resto aparece ao chegar perto
      if (this.equip.size > 24 && longe > 46 && this.selecionado !== id && (e.estado === "ok" || e.estado === "offline") && !(this.filtro && this.filtro.has(id))) el.style.display = "none";
      el.classList.toggle("completo", longe < 24 || this.selecionado === id || this.setorSelecionado === e.setor);
      if (el.style.display === "none") return;
      const w = el.offsetWidth, h = el.offsetHeight, x = parseFloat(el.style.left) - w / 2;
      let y = parseFloat(el.style.top) - h;
      for (let i = 0; i < 5; i++) { const o = sobrepoe({ x, y, w, h }); if (!o) break; y = o.y - h - 3; }
      el.style.top = `${y + h}px`;
      ocupados.push({ x, y, w, h });
    });
    this.setores.forEach((s, id) => {
      const el = s.rotDuto.el;
      if (el.style.display === "none") return;
      // rede grande: o texto "Rede sem dados" de longe só atrapalha
      if (this.setores.size > 8 && el.classList.contains("sd") && this.camera.position.distanceTo(this.ancoras.get(`s:${id}`)!()) > 46) { el.style.display = "none"; return; }
      const w = el.offsetWidth, h = el.offsetHeight, r = { x: parseFloat(el.style.left) - w / 2, y: parseFloat(el.style.top) - h / 2, w, h };
      if (sobrepoe(r)) el.style.display = "none"; else ocupados.push(r);
    });
    for (let i = this.bolhas.length - 1; i >= 0; i--) {
      const b = this.bolhas[i];
      if (agora > b.ate) { b.el.remove(); this.bolhas.splice(i, 1); continue; }
      const v = this.resolver(b.ancora);
      if (v) this.projetar(v.setY(v.y + 0.9), b.el, W, H, 8);
    }
  }

  private limparGrupo(g: THREE.Group) {
    this.pick = this.pick.filter((o) => { let p: THREE.Object3D | null = o; while (p) { if (p === g) return false; p = p.parent; } return true; });
    g.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.geometry) m.geometry.dispose();
      const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
      mats.forEach((x) => { if (!this.holos.includes(x as THREE.ShaderMaterial)) x.dispose(); });
    });
    g.clear();
  }

  /** Desmonta tudo (troca de rota). */
  descartar() {
    this.renderer.setAnimationLoop(null);
    this.obs.disconnect();
    const el = this.renderer.domElement;
    el.removeEventListener("pointermove", this.mover);
    el.removeEventListener("pointerleave", this.sair);
    el.removeEventListener("pointerdown", this.descer);
    el.removeEventListener("pointerup", this.subir);
    this.controles.dispose();
    this.limparGrupo(this.grupoRede);
    this.limparGrupo(this.grupoAtores);
    this.holos.forEach((m) => m.dispose());
    this.renderer.dispose();
    el.remove();
    this.camadaRotulos.remove();
  }
}

function escapar(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
